<template>
  <div class="vhcp-criterion-table">
    <div class="sensei-hint">
      <h4 v-if="criterion.senseiHintTitle()">
        <span class="number">{{ index + 1 }}</span>
        <span class="title-sep">-</span>
        <span class="text" v-html="criterion.senseiHintTitle()" />
        <span v-if="criterion.senseiHint()" class="toggle-hint" @click="SHOW_SENSEI_CRITERIA({ criteria: criterion, show: !showSenseiHint })">
          {{ showSenseiHint ? $t('message.VH_HIDE_SENSEI_HINT') : $t('message.VH_SHOW_SENSEI_HINT') }}
        </span>
      </h4>
      <div v-if="showSenseiHint && criterion.senseiHint()" v-html="criterion.senseiHint()" />
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="!showChart" :key="1" class="f-h" :style="tableStyle">
        <div class="tv-head" :style="tableHeadStyle">
          <div
            v-for="column in tableColumns"
            :key="column.key"
            class="tv-col"
            :class="{ ...column.sClass, 'show-sort': column.key === tableSort.key }"
            @click="setSort(column.key)"
          >
            <span class="tv-col-wrapper">
              <span
                v-if="column.key !== tableSort.key || !tableSort.desc"
                class="sort-options"
              >
                <Icon>{{ arrowUpFill }}</Icon>
                <Icon>{{ arrowDownThin }}</Icon>
              </span>
              <span v-else class="sort-options">
                <Icon>{{ arrowUpThin }}</Icon>
                <Icon>{{ arrowDownLine }}</Icon>
              </span>
              <span>{{ $t(`message.${column.titleTr}`) }}</span>
            </span>
          </div>
        </div>
        <div class="tv-rows">
          <VHCriterionTableRow
            v-for="group in teamsList"
            :key="`group${group.key}-${criterion.key}`"
            :criterion="criterion"
            :team="group"
            :display-score-column="displayScoreColumn"
            @teamClicked="(team) => $emit('teamClicked', team)"
            @valueClick="showChart = true"
            @scoreClick="showChart = true"
          />
        </div>
      </div>
      <VHubChartWrapper
        v-else
        :key="2"
        :criterion="criterion"
        :chart-scores-key="criterion.scoresKey"
        :chart-title="$t(`message.${criterion.titleTr}`)"
        :style="chartWrapperStyle"
        :groups="teamsList"
        @close="showChart = false"
      />
    </transition>
    <div class="ia-help-wrapper" @click="SHOW_SENSEI_CRITERIA({ criteria: criterion, show: !showSenseiHint })">
      <img :src="iaSensei" alt="">
    </div>
  </div>
</template>

<script>

/**
 * Vitality Hub criterion table.
 * A component to display details of teams about a specific piece of data
 *
 * @vue-prop {Object} criterion - Instance of VitalityCriterion
 * @vue-prop {Number} index - Index of the table within a category's criteria
 * @vue-prop {Number} minHeight - Minimum height in pixels of the table
 * @vue-prop {Number} maxHeight - Maximum height in pixels of the table
 * @vue-prop {Number} senseiHintHeight - Height in pixels of the sensei hint
 * @vue-prop {Array} [groups=null] - Groups to display in the table. If null, dislay all groups from state
 *
 */
import { isEqual, cloneDeep } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
import { SHOW_SENSEI_CRITERIA } from '@/store/modules/vitality/mutation-types';
// Components
import Icon from '@/components/icon.vue';
import VHCriterionTableRow from '@/components/vitality-hub/criterion-table-row.vue';
import VHubChartWrapper from '@/components/vitality-hub/chart-wrapper.vue';
// Utils
import {
  arrowDownThin,
  arrowDownLine,
  arrowUpThin,
  arrowUpFill,
} from '@/assets/icons';

const iaSensei = require('@/assets/img/ia-sensei.png');

const { mapState, mapMutations } = createNamespacedHelpers('vitality');

export default {
  name: 'VHCriterionTable',

  components: {
    Icon,
    VHCriterionTableRow,
    VHubChartWrapper,
  },

  props: {
    criterion: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    minHeight: {
      type: Number,
      default: 0,
    },
    maxHeight: {
      type: Number,
      default: 0,
    },
    groups: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      iaSensei,
      arrowDownThin,
      arrowDownLine,
      arrowUpThin,
      arrowUpFill,
      nameColKey: `ct_${this.criterion.key}`,
      valueColKey: `ct_${this.criterion.scoresKey}Value`,
      scoreColKey: `ct_${this.criterion.scoresKey}`,
      chartParam: `ct_${this.criterion.scoresKey}Chart`,
    };
  },

  computed: {
    /** Object: Style applied to the table */
    tableStyle() {
      return {
        minHeight: this.minHeight > 0 ? `${this.minHeight}px` : undefined,
        maxHeight: this.maxHeight > 0 ? `${this.maxHeight}px` : undefined,
      };
    },
    chartWrapperStyle() {
      return {
        height: this.maxHeight > 0 ? `${this.maxHeight}px` : undefined,
      };
    },
    /** Boolean: Some numbers are displayed only to inform the user */
    displayScoreColumn() {
      return this.criterion.weight > 0;
    },
    /** Object: Style applied to the head of each table */
    tableHeadStyle() {
      return {
        paddingRight: `${this.$store.state.device.scrollbarWidth}px`,
      };
    },
    /** String array: Columns of the main table */
    tableColumns() {
      const cols = [
        { key: this.nameColKey, titleTr: 'VH_TEAM_NAME', sClass: { name: true } },
        { key: this.valueColKey, titleTr: this.criterion.titleTr, sClass: { value: true } },
      ];
      if (this.displayScoreColumn) {
        cols.push({ key: this.scoreColKey, titleTr: 'VH_SCORE', sClass: { score: true } });
      }
      return cols;
    },
    /** Array: Sorted array of teams */
    teamsList() {
      const { key, desc } = this.tableSort;
      const list = cloneDeep(this.groups === null ? this.teamGroups : this.groups);
      const sk = this.criterion.scoresKey;
      if (list.length > 0 && !(list[0][sk] instanceof Array)) {
        return list;
      }

      switch (key) {
        case this.valueColKey: return desc
          ? list.sort((t1, t2) => t2[sk][0].data.value - t1[sk][0].data.value)
          : list.sort((t1, t2) => t1[sk][0].data.value - t2[sk][0].data.value);
        case this.scoreColKey: return desc
          ? list.sort((t1, t2) => t2[sk][0].score - t1[sk][0].score)
          : list.sort((t1, t2) => t1[sk][0].score - t2[sk][0].score);
        case this.nameColKey: return desc
          ? list.sort((t1, t2) => t2.name.localeCompare(t1.name))
          : list.sort((t1, t2) => t1.name.localeCompare(t2.name));
        default: return list;
      }
    },
    /** String: Sensei hint from criterion details */
    senseiHint() {
      return this.criterion.senseiHint();
    },
    /** Boolean: Should display sensei hint */
    showSenseiHint() {
      return !!this.$store.state.vitality.showSenseiForCriterion[this.criterion.key];
    },
    // ============
    // Query params
    // ============
    /** Object: Sorting order of the crit table { key: String, desc: Boolean} */
    tableSort: {
      get() {
        for (let i = 0, len = this.tableColumns.length; i < len; i++) {
          const { key } = this.tableColumns[i];
          if (this.$route.query[key] === 'true') {
            return { key, desc: this.$route.query[`${key}Desc`] === 'true' };
          }
        }
        return { key: this.scoreColKey, desc: false };
      },
      set(newVal) {
        const { key, desc } = newVal;
        if (this.tableColumns.map(c => c.key).indexOf(key) > -1) {
          const query = Object.assign({}, this.$route.query);
          this.tableColumns.forEach(({ key: k }) => {
            if (query[k]) {
              delete query[k];
            }
          });
          query[key] = 'true';
          query[`${key}Desc`] = desc ? 'true' : 'false';
          if (!isEqual(query, this.$route.query)) {
            this.$router.replace({ query });
          }
        }
      },
    },
    /**
     * Score columns can be clicked to display a chart
     * This is the key of the scores data for each team
     * @type {String}
     */
    showChart: {
      get() {
        return this.$route.query[this.chartParam] === 'true';
      },
      set(doShow) {
        const query = Object.assign({}, this.$route.query);
        query[this.chartParam] = doShow ? 'true' : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    ...mapState([
      'teamGroups',
    ]),
  },

  methods: {
    /**
     * Set the sort order to query params
     * @param {String} columnKey - key (unique string identifier) to recognise the
     *                             data over which values should be sorted
     */
    setSort(columnKey) {
      const { key, desc } = this.tableSort;
      const colDesc = key === columnKey ? !desc : false;
      this.tableSort = { key: columnKey, desc: colDesc };
    },
    /** Neo hint clicked: Display help about this criterion */
    helpClicked() {

    },
    ...mapMutations({
      SHOW_SENSEI_CRITERIA,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.vitality-hub .vhcp-criterion-table {
  position: relative;

  $vh-row-height: calc(2.8em + 10px);
  $vh-crit-table-head-color: #5e7ebd;


  .tv-head {
    color: #2A5A91;
    font-weight: 600;
    display: flex;
    padding-left: 3px;

    .tv-col {
      cursor: pointer;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }
  }
  .tv-rows {
    height: calc(100% - #{$vh-row-height});
    overflow: hidden;
    border: 3px solid #bbb;
    box-sizing: border-box;

    .tv-row {
      margin-top: 5px;
      display: flex;

      &:first-child {
        margin-top: 0px;
      }

      &:nth-child(odd) .tv-col {
        background: rgba(217, 227, 247, 1);
      }
      &:nth-child(even) .tv-col {
        background: rgba(217, 227, 247, 0.5);
      }
    }

    .hide-scrollbar {
      // Keeping these as comments as they come in handy when scrollbar hsould be removed
      // Uncomment and remove to make the scrollbar disappear
      // width: calc(100% + 20px);
      height: calc(100%);
      overflow-y: scroll;
      // padding-right: 20px;
    }
    .tv-col {
      min-width: 0px;
    }
  }

  .tv-col {
    flex: 1;
    text-align: center;
    padding: 5px 3px;
    box-sizing: border-box;
    margin-right: 5px;
    min-height: $vh-row-height;
    min-width: 0px;

    &:last-child {
      margin-right: 0px;
    }

    .tv-col-wrapper {
      position: relative;
      display: inline-block;
      width: 100%;
      padding: 0px 1.7em;
      box-sizing: border-box;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      .vit-category-icon {
        position: absolute;
        right: 0px;
        top: 50%;
        display: block;
        width: 1.5em;
        margin-left: 0.5em;
        transform: translateY(-50%);
      }
      .sort-options {
        display: none;
        position: absolute;
        left: 0px;
        top: 50%;
        margin-right: 0.5em;
        width: 1.2em;
        transform: translateY(-50%);

        svg {
          width: 0.6em;
          vertical-align: middle;
        }
      }
    }
    .variation-wrapper {
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;

      div.variation-indicator {
        font-size: 0.7em;

        svg.icon {
          width: 0.7em;
          margin-right: 0.2em;
          height: auto;

          &.up {
            color: $color-green-good;
          }
          &.down {
            color: $color-red-bad;
          }
        }
      }
      > span {
        margin-right: 0.5em;
      }
    }
    &.show-sort, &:hover {
      .tv-col-wrapper .sort-options {
        display: inline;
      }
    }

    &.clickable {
      cursor: pointer;
      position: relative;

      &::after {
        content: " ";
        position: absolute;
        top: 0px;
        right: 0px;
        bottom: 0px;
        left: 0px;
        background: rgba(0, 0, 0, 0.1);
        display: none;
      }
      &:hover {
        &::after {
          display: block;
        }
      }
    }
  }

  .tv-col {
    min-height: calc(2.8em + 10px);
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    &:not(:last-child) {
      margin-right: 1%;
    }
    &.name {
      flex-basis: 24%;
    }
    &.value {
      flex-basis: 54%;
    }
    &.score {
      flex-basis: 19%;
    }
  }

  .tv-rows {
    max-height: calc(calc(100% - #{$vh-row-height}) - 5px);
    overflow-y: scroll;
    border: none;
    height: auto;

    .tv-row {
      &:nth-child(odd) .tv-col.good {
        background: rgba(176, 255, 164, 1);
      }
      &:nth-child(even) .tv-col.good {
        background: rgba(176, 255, 164, 0.5);
      }

      &:nth-child(odd) .tv-col.bad {
        background: rgba(255, 189, 189, 1);
      }
      &:nth-child(even) .tv-col.bad {
        background: rgba(255, 189, 189, 0.5);
      }

      .tv-col {
        white-space: normal;

        &.value {
          white-space: pre-line;
        }
      }
    }
  }
  .tv-head {
    padding: 0px;
    flex-flow: row wrap;
    margin-bottom: 5px;

    .tv-col {
      background: $vh-crit-table-head-color;
      color: white;
      font-weight: 600;
      border: 2px solid $vh-crit-table-head-color;
      padding: 3px;
      transition: background .1s linear, color .1s linear;
      height: calc(2.8em + 10px);

      &:hover {
        color: $vh-crit-table-head-color;
        background: #ffffff;
      }


      .tv-col-wrapper {
        padding: 0px 1.3em;
        white-space: normal;

        .sort-options {
          margin-right: 0.4em;
          width: 1em;

          svg {
            width: 0.5em;
          }
        }
      }
      &.show-sort, &:hover {
        .tv-col-wrapper .sort-options {
          display: inline;
        }
      }
    }
  }

  .ia-help-wrapper {
    position: absolute;
    top: -1.3em;
    right: -0.5em;
    width: 3em;
    height: 3em;
    background: white;
    border-radius: 2em;
    box-shadow: 0px 0px 13px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.15s;

    img {
      width: 100%;
      height: 100%;
    }
    &:hover {
      background: #F5F5F5;
      box-shadow: 0px 0px 18px rgba(0, 0, 0, 0.2);
    }
  }

  .sensei-hint {
    @include sensei-texts;

    h4 {
      font-weight: 550;
      color: #29a4ef;
      font-size: 1.1em;
      line-height: 1.5em;
      text-indent: 1em;
      margin: 0px;
      margin-right: 2em;

      .title-sep {
        margin: 0px 0.2em;
      }
      .toggle-hint {
        margin-left: 0.5em;
        font-style: italic;
        font-size: 0.9em;
        line-height: 1.4em;
        cursor: pointer;
        font-weight: normal;
        color: $color-blue-text;
      }
    }

    margin-bottom: 0.5em;
  }
}
</style>
